/** Grouped navigation component with collapsible sections. */
import React, { useState, useEffect, ReactNode } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Assessment as BusinessKpisIcon,
  AccountCircle as AccountIcon,
  People as ResellersIcon,
  Business as WholesaleIcon,
  Dialpad as DialpadIcon,
  PhoneForwarded as DidsIcon,
  Receipt as InvoicesIcon,
  Payment as PaymentsIcon,
  LocalOffer as RatePlansIcon,
  Description as TaxReportsIcon,
  Router as CarriersIcon,
  Phone as SuppliersIcon,
  SettingsInputComponent as GatewaysIcon,
  Sort as LcrIcon,
  CompareArrows as RateComparisonIcon,
  AccountBalance as BillingIcon,
  Rule as LcrRulesIcon,
  HistoryEdu as AuditIcon,
  List as CdrsIcon,
  TrendingUp as TrafficIcon,
  AccountBalance as FinancialReportsIcon,
  Storage as CgratesStatusIcon,
  VpnKey as ApiKeysIcon,
  PhoneInTalk as TestCallIcon,
  SettingsPhone as ActiveCallsIcon,
  NotificationsActive as AlertsIcon,
  Group as TeamIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

/** Navigation item structure. */
interface NavItem {
  text: string;
  icon: ReactNode;
  path: string;
  badge?: string;
  /** Actor roles that see this item (localStorage auth_role). Items
   *  without roles are admin-only — the safe default for admin surfaces. */
  roles?: string[];
}

/** Navigation group structure. */
interface NavGroup {
  group: string;
  items: NavItem[];
}

/** Navigation groups organized by business function.
 * Exported for reuse by the command palette (UX review A3). */
export const NAV_GROUPS: NavGroup[] = [
  {
    group: "Overview",
    items: [
      { text: "Dashboard", icon: <DashboardIcon />, path: "/", roles: ["admin", "operator", "finance"] },
      { text: "Business KPIs", icon: <BusinessKpisIcon />, path: "/business-kpis" },
      { text: "Active Calls", icon: <ActiveCallsIcon />, path: "/active-calls", roles: ["admin", "operator"] },
    ],
  },
  {
    group: "Customers",
    items: [
      { text: "Accounts", icon: <AccountIcon />, path: "/accounts" },
      { text: "Resellers", icon: <ResellersIcon />, path: "/resellers" },
      { text: "Wholesale Clients", icon: <WholesaleIcon />, path: "/wholesale" },
      { text: "PIN Customers", icon: <DialpadIcon />, path: "/pin-customers" },
      { text: "DIDs", icon: <DidsIcon />, path: "/dids" },
    ],
  },
  {
    group: "Billing & Finance",
    items: [
      { text: "Billing", icon: <BillingIcon />, path: "/billing", roles: ["admin", "finance"] },
      { text: "Invoices", icon: <InvoicesIcon />, path: "/invoices", roles: ["admin", "finance"] },
      { text: "Payments", icon: <PaymentsIcon />, path: "/payments", roles: ["admin", "finance"] },
      { text: "Tax Reports", icon: <TaxReportsIcon />, path: "/tax-reports", roles: ["admin", "finance"] },
    ],
  },
  {
    group: "Routing",
    items: [
      { text: "Route Workspace", icon: <LcrIcon />, path: "/routing" },
      { text: "Suppliers", icon: <SuppliersIcon />, path: "/suppliers" },
      { text: "Gateways", icon: <GatewaysIcon />, path: "/gateways" },
      { text: "Rate Plans", icon: <RatePlansIcon />, path: "/rate-plans" },
      { text: "Rate Comparison", icon: <RateComparisonIcon />, path: "/rate-comparison" },
    ],
  },
  {
    group: "Reports",
    items: [
      { text: "CDRs", icon: <CdrsIcon />, path: "/cdrs", roles: ["admin", "operator", "finance"] },
      { text: "Traffic Analysis", icon: <TrafficIcon />, path: "/traffic", roles: ["admin", "operator"] },
      { text: "Financial Reports", icon: <FinancialReportsIcon />, path: "/financial-reports", roles: ["admin", "finance"] },
    ],
  },
  {
    group: "System",
    items: [
      // Settings/Users/ApiKeys hidden in Wave 1.4/1.5/1.7 — pages were
      // placeholders or mock-data. Return with real backends (Wave 2.8, 5.5, 5.7).
      { text: "Billing Engine Status", icon: <CgratesStatusIcon />, path: "/cgrates/status", roles: ["admin", "operator"] },
      { text: "Team", icon: <TeamIcon />, path: "/team" },
      { text: "API Keys", icon: <ApiKeysIcon />, path: "/api-keys" },
      { text: "Alerts", icon: <AlertsIcon />, path: "/alerts", roles: ["admin", "operator", "finance"] },
      { text: "Audit Log", icon: <AuditIcon />, path: "/audit" },
      { text: "Test Call", icon: <TestCallIcon />, path: "/test-call", roles: ["admin", "operator"] },
    ],
  },
];

/** LocalStorage key for expanded groups. */
const EXPANDED_GROUPS_KEY = "nav_expanded_groups";

/** Grouped navigation component with collapsible sections. */
export default function GroupedNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(["Overview"]);

  /** Actor role from the login/me payload (RBAC nav filtering).
   *  Unknown/absent role falls back to "admin" — the legacy behavior. */
  const role = (localStorage.getItem("auth_role") || "admin").toLowerCase();
  const visibleGroups = NAV_GROUPS.map((navGroup) => ({
    ...navGroup,
    items: navGroup.items.filter(
      (item) => !item.roles || item.roles.includes(role) || role === "admin"
    ),
  })).filter((navGroup) => navGroup.items.length > 0);

  /** Load expanded groups from localStorage on mount. */
  useEffect(() => {
    const saved = localStorage.getItem(EXPANDED_GROUPS_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setExpandedGroups(parsed);
        }
      } catch (e) {
        console.error("Failed to parse expanded groups from localStorage:", e);
      }
    }
  }, []);

  /** Save expanded groups to localStorage when they change. */
  useEffect(() => {
    localStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  /** Toggle group expansion. */
  const handleToggleGroup = (group: string) => {
    setExpandedGroups((prev) =>
      prev.includes(group)
        ? prev.filter((g) => g !== group)
        : [...prev, group]
    );
  };

  /** Check if a nav item is active. */
  const isItemActive = (path: string) => {
    // Exact match for root path
    if (path === "/") {
      return location.pathname === "/";
    }
    // Prefix match for other paths
    return location.pathname.startsWith(path);
  };

  return (
    <List sx={{ width: "100%", bgcolor: "background.paper" }}>
      {visibleGroups.map((navGroup) => {
        const isExpanded = expandedGroups.includes(navGroup.group);

        return (
          <Box key={navGroup.group}>
            {/* Group header
                Wave 3.2 (UX review §1.2/§1.3): group headers are disclosure
                controls, not navigation targets. They never look "selected"
                and render as small uppercase muted labels with a chevron —
                visually distinct from the nav items below them. */}
            <ListItemButton
              onClick={() => handleToggleGroup(navGroup.group)}
              aria-expanded={isExpanded}
              sx={{
                bgcolor: "transparent", // never highlighted, even when a child is active
                "&:hover": { bgcolor: "action.hover" },
                "&.Mui-selected": { bgcolor: "transparent" },
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "text.secondary",
                    }}
                  >
                    {navGroup.group}
                  </Typography>
                }
              />
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </ListItemButton>

            {/* Group items */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {navGroup.items.map((item) => (
                  <ListItemButton
                    key={item.text}
                    onClick={() => navigate(item.path)}
                    selected={isItemActive(item.path)}
                    sx={{
                      pl: 4,
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "white",
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isItemActive(item.path) ? "inherit" : "inherit",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                    {item.badge && (
                      <Typography
                        variant="caption"
                        sx={{
                          ml: 1,
                          px: 1,
                          py: 0.25,
                          bgcolor: "secondary.main",
                          color: "secondary.contrastText",
                          borderRadius: 1,
                          fontSize: "0.7rem",
                        }}
                      >
                        {item.badge}
                      </Typography>
                    )}
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </List>
  );
}
